import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCalendar, faClock, faSearch } from '@fortawesome/free-solid-svg-icons';
import { Upload, Link as LinkIcon, Share as ShareIcon } from 'lucide-react';
import { getGradientColors } from '../../utils/communityHelpers';
import { createShareLinks } from '../../config';

/**
 * CommunityHeader Component
 * Top navigation, cover image, logo, and action buttons
 */
const CommunityHeader = ({ 
  community, 
  isUserMember, 
  membershipStatus,
  onJoinClick,
  onDonateClick,
  onCalendarClick,
  onZmanimClick,
  onSearchClick,
  onGetAppClick,
  showBackButton = false,
  activeTab,
  onTabChange
}) => {
  const navigate = useNavigate();
  const [showMobileShareMenu, setShowMobileShareMenu] = useState(false);
  
  // Get gradient colors if no cover image
  const gradientColors = getGradientColors(community?.name || 'Community');
  
  // Get community initials for fallback logo
  const getInitials = (name) => {
    if (!name) return 'C';
    const words = name.split(' ');
    if (words.length >= 2) {
      return words[0][0] + words[1][0];
    }
    return name.substring(0, 2);
  };
  
  const handleBackClick = () => {
    navigate(-1);
  };
  
  // Handle share group
  const handleShareGroup = async () => {
    const shareUrl = `https://yesod.us/community/${community?.slug || community?.id}`;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      showCopyToast();
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  // Show copy toast notification
  const showCopyToast = () => {
    const toast = document.createElement('div');
    toast.className = 'copy-toast';
    toast.textContent = 'Link copied to clipboard!';
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.remove();
    }, 3000);
  };
  
  // Handle mobile share menu
  const handleMobileMenuClick = () => {
    setShowMobileShareMenu(true);
  };
  
  const handleCopyLink = async () => {
    const shareUrl = `https://yesod.us/community/${community?.slug || community?.id}`;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShowMobileShareMenu(false);
      // Show toast after closing modal
      setTimeout(() => {
        showCopyToast();
      }, 100);
    } catch (err) {
      console.error('Failed to copy link:', err);
      setShowMobileShareMenu(false);
    }
  };
  
  const handleNativeShare = async () => {
    const shareUrl = `https://yesod.us/community/${community?.slug || community?.id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join ${community?.name || 'Community'} on Yesod`,
          text: `You've been invited to join ${community?.name || 'this community'}`,
          url: shareUrl
        });
        setShowMobileShareMenu(false);
      } catch (err) {
        // User cancelled or error occurred
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err);
          // Fallback to copy on error
          try {
            await navigator.clipboard.writeText(shareUrl);
            setShowMobileShareMenu(false);
            setTimeout(() => {
              showCopyToast();
            }, 100);
          } catch (copyErr) {
            console.error('Failed to copy link:', copyErr);
            setShowMobileShareMenu(false);
          }
        } else {
          // User just cancelled, close menu
          setShowMobileShareMenu(false);
        }
      }
    } else {
      // Fallback to copy
      try {
        await navigator.clipboard.writeText(shareUrl);
        setShowMobileShareMenu(false);
        setTimeout(() => {
          showCopyToast();
        }, 100);
      } catch (err) {
        console.error('Failed to copy link:', err);
        setShowMobileShareMenu(false);
      }
    }
  };
  
  const renderActionButton = () => {
    // Always show "Get the App" button
    return (
      <button 
        className="community-join-btn get-app"
        onClick={onGetAppClick}
      >
        Get the App
      </button>
    );
  };
  
  const tabs = [
    { id: 0, name: 'Updates' },
    { id: 1, name: 'Events' },
    { id: 2, name: 'Classes' },
    { id: 3, name: 'Prayer' }
  ];

  return (
    <div className="community-header">
      {/* Top Navigation Bar - Mobile */}
      <div className="community-top-nav mobile-nav">
        <button 
          className="community-nav-btn" 
          onClick={showBackButton ? handleBackClick : handleMobileMenuClick}
          aria-label={showBackButton ? "Back" : "Menu"}
        >
          {showBackButton ? (
            <FontAwesomeIcon icon={faArrowLeft} />
          ) : (
            <span style={{ fontSize: '24px', fontWeight: 700 }}>⋯</span>
          )}
        </button>
        
        <div className="community-nav-title">
          {community?.name || 'Community'}
        </div>
        
        <button 
          className="community-nav-btn"
          onClick={onSearchClick}
          aria-label="Search community"
        >
          <FontAwesomeIcon icon={faSearch} />
        </button>
      </div>
      
      {/* Desktop Header with Logo, Name, Tabs, and Get App Button */}
      <div className="community-top-nav desktop-nav">
        <div className="desktop-logo-name">
          {community?.logo_url && community.logo_url.trim() !== '' ? (
            <img 
              src={community.logo_url}
              alt={`${community.name} logo`}
              className="desktop-logo"
            />
          ) : (
            <div className="desktop-logo-initials">
              {getInitials(community?.name)}
            </div>
          )}
          <h1 className="desktop-community-name">{community?.name || 'Community'}</h1>
        </div>
        
        <div className="desktop-header-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`community-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => onTabChange(tab.id)}
            >
              <span className="community-tab-text">{tab.name}</span>
              {activeTab === tab.id && <div className="community-tab-underline" />}
            </button>
          ))}
        </div>
        
        <div className="desktop-header-actions">
          <button 
            className="community-share-btn"
            onClick={handleShareGroup}
            aria-label="Share community"
          >
            <Upload size={18} />
          </button>
          {renderActionButton()}
        </div>
      </div>
      
      {/* Cover Image */}
      {community?.cover_image_url && community.cover_image_url.trim() !== '' ? (
        <img 
          src={community.cover_image_url} 
          alt={`${community.name} cover`}
          className="community-cover"
        />
      ) : (
        <div 
          className="community-cover-gradient"
          style={{
            '--gradient-start': gradientColors[0],
            '--gradient-end': gradientColors[1],
            background: `linear-gradient(135deg, ${gradientColors[0]}, ${gradientColors[1]})`
          }}
        />
      )}
      
      {/* Logo and Actions Row */}
      <div className="community-logo-row">
        {community?.logo_url && community.logo_url.trim() !== '' ? (
          <img 
            src={community.logo_url}
            alt={`${community.name} logo`}
            className="community-logo"
          />
        ) : (
          <div className="community-logo-initials">
            {getInitials(community?.name)}
          </div>
        )}
        
        <div className="community-actions">
          <button 
            className="community-action-circle"
            onClick={onCalendarClick}
            aria-label="Open calendar"
          >
            <FontAwesomeIcon icon={faCalendar} size="lg" />
          </button>
          
          <button 
            className="community-action-circle"
            onClick={onZmanimClick}
            aria-label="View zmanim times"
          >
            <FontAwesomeIcon icon={faClock} size="lg" />
          </button>
          
          {renderActionButton()}
        </div>
      </div>
      
      {/* Mobile Share Menu Modal */}
      {showMobileShareMenu && (
        <>
          <div 
            className="mobile-share-overlay" 
            onClick={() => setShowMobileShareMenu(false)}
          />
          <div className="mobile-share-menu">
            <div className="mobile-share-header">
              <h3>Share Community</h3>
              <button 
                className="mobile-share-close"
                onClick={() => setShowMobileShareMenu(false)}
              >
                ✕
              </button>
            </div>
            <div className="mobile-share-options">
              <button 
                className="mobile-share-option"
                onClick={handleCopyLink}
              >
                <LinkIcon size={20} />
                <span>Copy Link</span>
              </button>
              <button 
                className="mobile-share-option"
                onClick={handleNativeShare}
              >
                <ShareIcon size={20} />
                <span>Share via...</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CommunityHeader;
